import { z } from 'zod';
import {
  ADJUSTMENT_KINDS,
  ATTENDANCE_STATUSES,
  JOB_CONTRACT_STATUSES,
  DEPARTMENT_DEFS,
  EMPLOYEE_STATUSES,
  LEAVE_STATUSES,
  LEAVE_TYPES,
} from './hr.js';
import { WORK_MODES } from './orgsetup.js';
import { EMPLOYMENT_TYPES } from './recruit.js';

/** مخططات وحدة الموارد البشرية — مشتركة بين الخادم والواجهة. */

/** مفاتيحُ الأقسام كصفٍّ ثابت — `z.enum` يطلب صفًّا لا مصفوفة. */
const DEPARTMENT_KEYS_TUPLE = DEPARTMENT_DEFS.map((d) => d.key) as unknown as [
  string,
  ...string[],
];

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional()
  .refine((v) => v == null || z.string().email().safeParse(v).success, {
    message: 'صيغة البريد الإلكتروني غير صحيحة.',
  });

const optionalPhone = z
  .string()
  .trim()
  .max(32)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional()
  .refine((v) => v == null || /^[\d\s+()-]{6,32}$/.test(v), {
    message: 'رقم الهاتف يحتوي على رموز غير مقبولة.',
  });

const money = z
  .number()
  .min(0, 'القيمة لا يمكن أن تكون سالبة.')
  .max(99_999_999_999.99, 'القيمة أكبر من الحد المسموح.');

const optionalDate = z.coerce.date().nullable().optional();

/**
 * تاريخ بلا وقت.
 *
 * أعمدة التاريخ في الحضور والإجازات من نوع date لا timestamp. تمرير كائن
 * Date كامل يحمل توقيت الجلسة فينزلق اليوم عند فارق التوقيت — وقد وقع هذا
 * فعلًا في نطاقات التقارير. النص هنا هو الشكل الوحيد المقبول.
 */
const DayString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ يجب أن يكون بصيغة سنة-شهر-يوم.');

/* ==========================================================================
   الموظفون
   ========================================================================== */

export const CreateEmployeeSchema = z.object({
  name: z.string().trim().min(2, 'الاسم مطلوب.').max(120),
  code: optionalText(32),
  jobTitle: optionalText(120),
  department: optionalText(120),
  /** قسمُ العمل من الكتالوج — به يُعرف أين يظهر الموظّف. */
  deptKey: z.enum(DEPARTMENT_KEYS_TUPLE).nullable().optional(),
  email: optionalEmail,
  phone: optionalPhone,
  nationalId: optionalText(32),
  birthDate: optionalDate,
  address: optionalText(300),

  hireDate: z.coerce.date({ errorMap: () => ({ message: 'تاريخ التعيين مطلوب.' }) }),
  endDate: optionalDate,
  status: z.enum(EMPLOYEE_STATUSES).default('ACTIVE'),

  baseSalary: money.default(0),
  currency: z.string().trim().length(3).default('EGP'),

  isSalesRep: z.boolean().default(false),
  commissionRate: z.number().min(0).max(100).nullable().optional(),
  salesTarget: money.nullable().optional(),

  managerId: Cuid.nullable().optional(),
  membershipId: Cuid.nullable().optional(),
  annualLeaveBalance: z.number().min(0).max(365).default(21),

  /*
   * التأمينات وضريبة الدخل.
   *
   * لكلّ موظّفٍ على حدة: متدرّبٌ أو متعاقدٌ بالقطعة لا يُؤمَّن عليه،
   * وتفعيلُها للجميع يخصم من راتب من لا تأمينَ له.
   */
  insured: z.boolean().default(false),
  insuranceNumber: optionalText(40),
  /** الأجرُ التأمينيّ — فارغُه يعني «على الأساسيّ». */
  insuranceWage: money.nullable().optional(),
  taxable: z.boolean().default(false),

  /* ما يُختار من إعداد المؤسسة — الوحدةُ ومركزُ التكلفة والورديّةُ ونمطُ العمل. */
  orgUnitId: Cuid.nullable().optional(),
  costCenterId: Cuid.nullable().optional(),
  scheduleId: Cuid.nullable().optional(),
  workMode: z.enum(WORK_MODES).default('ONSITE'),
  /*
   * ما يُختار من التطوير التنظيميّ.
   *
   * بالوصف المعتمَد يُعدّ الموظّفُ في «المشغول» أمام «المعتمَد» في تخطيط
   * القوى العاملة، وبالدرجة تُقارن نسبةُ راتبه إلى وسط درجته. وكانا
   * يُكتبان في قاعدة البيانات ولا سبيلَ إلى ضبطهما من الشاشة.
   */
  positionId: Cuid.nullable().optional(),
  gradeId: Cuid.nullable().optional(),
});

export type CreateEmployeeInput = z.infer<typeof CreateEmployeeSchema>;

export const UpdateEmployeeSchema = CreateEmployeeSchema.partial();
export type UpdateEmployeeInput = z.infer<typeof UpdateEmployeeSchema>;

export const EmployeeListQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.enum(EMPLOYEE_STATUSES).optional(),
  department: z.string().trim().max(120).optional(),
  deptKey: z.string().trim().max(40).optional(),
  isSalesRep: z.coerce.boolean().optional(),
  managerId: Cuid.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  sort: z.string().trim().max(40).optional(),
  order: z.enum(['asc', 'desc']).default('asc'),
});

export type EmployeeListQuery = z.infer<typeof EmployeeListQuerySchema>;

/* ==========================================================================
   الحضور
   ========================================================================== */

export const AttendanceRowSchema = z.object({
  employeeId: Cuid,
  status: z.enum(ATTENDANCE_STATUSES),
  checkIn: z.string().regex(/^\d{2}:\d{2}$/, 'الوقت بصيغة ساعة:دقيقة.').nullable().optional(),
  checkOut: z.string().regex(/^\d{2}:\d{2}$/, 'الوقت بصيغة ساعة:دقيقة.').nullable().optional(),
  lateMinutes: z.number().int().min(0).max(1440).default(0),
  overtimeMinutes: z.number().int().min(0).max(1440).default(0),
  notes: optionalText(300),
});

/**
 * تسجيل الحضور ليوم كامل دفعة واحدة.
 *
 * الشاشة تعرض الفريق كله في يوم واحد وتُحفظ مرة واحدة. الحفظ صفًا صفًا
 * يترك اليوم نصف مسجَّل إن انقطع الاتصال في المنتصف.
 */
export const SaveAttendanceSchema = z.object({
  date: DayString,
  rows: z.array(AttendanceRowSchema).min(1, 'لا توجد صفوف للحفظ.').max(500),
});

export type SaveAttendanceInput = z.infer<typeof SaveAttendanceSchema>;

export const AttendanceQuerySchema = z
  .object({
    from: DayString,
    to: DayString,
    employeeId: Cuid.optional(),
    department: z.string().trim().max(120).optional(),
  })
  .refine((v) => v.from <= v.to, { message: 'تاريخ البداية بعد تاريخ النهاية.' });

export type AttendanceQuery = z.infer<typeof AttendanceQuerySchema>;

export const AttendanceDayQuerySchema = z.object({
  date: DayString,
  department: z.string().trim().max(120).optional(),
});

export type AttendanceDayQuery = z.infer<typeof AttendanceDayQuerySchema>;

/* ==========================================================================
   الإجازات
   ========================================================================== */

export const CreateLeaveSchema = z
  .object({
    employeeId: Cuid,
    type: z.enum(LEAVE_TYPES),
    fromDate: DayString,
    toDate: DayString,
    reason: optionalText(500),
  })
  .refine((v) => v.fromDate <= v.toDate, {
    message: 'تاريخ البداية بعد تاريخ النهاية.',
    path: ['toDate'],
  });

export type CreateLeaveInput = z.infer<typeof CreateLeaveSchema>;

export const DecideLeaveSchema = z.object({
  status: z.enum(['APPROVED', 'REJECTED'], {
    errorMap: () => ({ message: 'القرار إما اعتماد أو رفض.' }),
  }),
  decisionNote: optionalText(500),
});

export type DecideLeaveInput = z.infer<typeof DecideLeaveSchema>;

export const LeaveListQuerySchema = z.object({
  status: z.enum(LEAVE_STATUSES).optional(),
  type: z.enum(LEAVE_TYPES).optional(),
  employeeId: Cuid.optional(),
  from: DayString.optional(),
  to: DayString.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});

export type LeaveListQuery = z.infer<typeof LeaveListQuerySchema>;

/* ==========================================================================
   بنود المرتب
   ========================================================================== */

export const CreateAdjustmentSchema = z.object({
  employeeId: Cuid,
  kind: z.enum(ADJUSTMENT_KINDS),
  /** المبلغ موجب دائمًا؛ الإشارة من النوع لا من الرقم. */
  amount: money.refine((v) => v > 0, 'المبلغ يجب أن يكون أكبر من صفر.'),
  period: DayString,
  reason: optionalText(300),
});

export type CreateAdjustmentInput = z.infer<typeof CreateAdjustmentSchema>;

export const AdjustmentListQuerySchema = z.object({
  employeeId: Cuid.optional(),
  period: DayString.optional(),
  kind: z.enum(ADJUSTMENT_KINDS).optional(),
});

export type AdjustmentListQuery = z.infer<typeof AdjustmentListQuerySchema>;

/* ==========================================================================
   دورات المرتب
   ========================================================================== */

export const CreatePayrollRunSchema = z.object({
  period: DayString,
  notes: optionalText(500),
});

export type CreatePayrollRunInput = z.infer<typeof CreatePayrollRunSchema>;

export const PayrollRunListQuerySchema = z.object({
  status: z.enum(['DRAFT', 'APPROVED', 'PAID']).optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});

export type PayrollRunListQuery = z.infer<typeof PayrollRunListQuerySchema>;

/* ==========================================================================
   عقود العمل
   ========================================================================== */

/**
 * عقدُ عمل.
 *
 * والتوقيعُ لا يُكتب هنا: له مسارُه — وضعُ «تم التوقيع» في نموذجٍ يُحفظ
 * مع بقيّة الحقول كان يجعل تعديلَ بندٍ في عقدٍ موقَّعٍ يُعيد إطلاقِ ما
 * يقع عند التوقيع، أو يُخفيه. وهو الذي يجعل الموظّفَ على رأس العمل.
 */
export const UpsertJobContractSchema = z
  .object({
    employeeId: Cuid,
    employmentType: z.enum(EMPLOYMENT_TYPES).default('FULL_TIME'),
    startsOn: DayString,
    /** فارغُها عقدٌ غيرُ محدّد المدّة. */
    endsOn: DayString.nullable().optional(),
    probationMonths: z.coerce
      .number()
      .int()
      .min(0)
      .max(12, 'فترة الاختبار أطول من سنة.')
      .default(3),
    baseSalary: money,
    currency: z.string().trim().length(3).default('EGP'),
    terms: optionalText(20_000),
    offerId: Cuid.nullable().optional(),
  })
  .refine((v) => !v.endsOn || v.endsOn > v.startsOn, {
    message: 'نهاية العقد قبل بدايته.',
    path: ['endsOn'],
  });
export type UpsertJobContractInput = z.infer<typeof UpsertJobContractSchema>;

/** تعديلُ عقدٍ قائم — بلا ربطٍ بعرضٍ ولا تغييرِ صاحبه. */
export const UpdateJobContractSchema = z
  .object({
    employmentType: z.enum(EMPLOYMENT_TYPES).optional(),
    startsOn: DayString.optional(),
    endsOn: DayString.nullable().optional(),
    probationMonths: z.coerce.number().int().min(0).max(12).optional(),
    baseSalary: money.optional(),
    currency: z.string().trim().length(3).optional(),
    terms: optionalText(20_000),
  })
  .refine((v) => !v.endsOn || !v.startsOn || v.endsOn > v.startsOn, {
    message: 'نهاية العقد قبل بدايته.',
    path: ['endsOn'],
  });
export type UpdateJobContractInput = z.infer<typeof UpdateJobContractSchema>;

/**
 * تغييرُ حالةِ العقد.
 *
 * التوقيعُ واقعةٌ لها تاريخٌ ومن سجّلها: «وُقّع» بلا تاريخٍ تجعل بدايةَ
 * الالتزام مجهولة، وهي ما يُحتسب منه رصيدُ الإجازة وفترةُ الاختبار.
 * والإنهاءُ قبل المدّة يطلب سببًا — إنهاءٌ بلا سبب لا يُراجَع بعد سنة.
 */
export const JobContractStatusSchema = z
  .object({
    status: z.enum(JOB_CONTRACT_STATUSES),
    signedOn: DayString.nullable().optional(),
    endReason: optionalText(1000),
  })
  .refine((v) => v.status !== 'SIGNED' || !!v.signedOn, {
    message: 'تاريخ التوقيع مطلوبٌ عند تعليم العقد موقَّعًا.',
    path: ['signedOn'],
  })
  .refine((v) => v.status !== 'TERMINATED' || !!v.endReason, {
    message: 'سبب الإنهاء مطلوب.',
    path: ['endReason'],
  });
export type JobContractStatusInput = z.infer<typeof JobContractStatusSchema>;

export const JobContractsQuerySchema = z.object({
  status: z.enum(JOB_CONTRACT_STATUSES).optional(),
  employeeId: Cuid.optional(),
  q: z.string().trim().max(120).optional(),
  /** العقودُ التي تنتهي قريبًا — أو التي انقضت ولم تُغلق. */
  expiring: z.enum(['1']).optional(),
});
export type JobContractsQuery = z.infer<typeof JobContractsQuerySchema>;
